import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    IconButton,
    TextField,
    Button,
    MenuItem,
    Slider,
    Divider,
    Autocomplete,
} from '@mui/material';
import { EditRounded, CheckCircleOutline, Cancel, AddCircleOutline, ArrowDropDown } from '@mui/icons-material';
import { IWorkProfile, TProfileType, TSkill } from '../../types/global/helper';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';
import LocationAutocomplete from '../LocationAutocomplete/LocationAutocomplete';
import { CORE_SKILLS, ETHNICITIES, LANGUAGES } from '../FindTalentsContainer/filterEnums';
import { SocialMediaSection } from './SocialMediaSection';
import { RichTextViewer } from '../Common/RichTextViewer';
import { RichTextEditor } from '../Common/RichTextEditor';

export interface ICastingDetails {
    profile: IWorkProfile;
    themeStyle: ThemeStyle;
    isEditMode?: boolean;
    onEditClick?: VoidFunction;
    previewMode?: boolean;
    onSubmitForm?: (data: any) => void;
}

const ATTR_BODY_TYPES = ['athletic', 'thin', 'curvy', 'average', 'any'];

const MAX_LENGTH = 40;

type SectionKey = keyof IWorkProfile | 'age_range' | 'attributes';

// Define the hidden fields per profile type
const hiddenFieldsByProfileType: Record<TProfileType, (keyof IWorkProfile)[]> = {
    technical: ['stage_name', 'ethnicity'],
    content_creator: [], // add rules later if needed
    voiceover: [],
    performer: [],
    artist: [],
};

const hiddenSectionsByProfileType: Record<TProfileType, string[]> = {
    technical: ['age_range', 'attributes'], // 👈 exclude "age_range" and "attributes" section for content creators
    content_creator: ['age_range', 'attributes'],
    voiceover: [],
    performer: [],
    artist: [],
};

export const CastingDetails: React.FC<ICastingDetails> = ({
    profile,
    themeStyle,
    previewMode = true,
    onSubmitForm,
}) => {
    // Section edit states
    const [editingSections, setEditingSections] = useState<{ [key: string]: boolean }>({});
    // Local state for editing
    const [about, setAbout] = useState(profile?.about || '');
    const [title, setTitle] = useState(profile?.title || '');
    const [stageName, setStageName] = useState(profile?.stage_name || '');
    const [ageRange, setAgeRange] = useState<[number, number]>([profile?.age_from || 18, profile?.age_to || 40]);
    const [location, setLocation] = useState(
        profile?.location || { address: '', description: '', coordinates: [], type: '', short_name: '' },
    );
    const [attributes, setAttributes] = useState({
        gender: profile?.gender || '',
        sex: profile?.sex || '',
        body_type: profile?.body_type || '',
        hair_color: profile?.hair_color || '',
        height: profile?.height || 0,
        weight: profile?.weight || 0,
    });
    // Skills/Other Skills state
    const [skills, setSkills] = useState<TSkill[]>(profile?.skills || []);
    const [otherSkills, setOtherSkills] = useState<TSkill[]>(profile?.other_skills || []);
    const [newSkill, setNewSkill] = useState<TSkill>({
        skill: '',
        expertise: 'beginner',
        years_of_experience: 1,
        description: '',
    });
    const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null);
    const [languages, setLanguages] = useState<string[]>(profile?.language_spoken || []);
    const [newLanguage, setNewLanguage] = useState('');
    const [ethnicity, setEthnicity] = useState<string[]>(profile?.ethnicity || []);
    const [newEthnicity, setNewEthnicity] = useState('');
    const [specialSkills, setSpecialSkills] = useState<string[]>(profile?.special_skills || []);
    const [newSpecialSkill, setNewSpecialSkill] = useState('');
    const [links, setLinks] = useState<{ link: string; description: string }[]>(profile?.links || []);
    const [newLink, setNewLink] = useState<{ link: string; description: string }>({ link: '', description: '' });
    const [editingLinkIndex, setEditingLinkIndex] = useState<number | null>(null);
    const [showAddSkillForm, setShowAddSkillForm] = useState(false);
    const [showAddOtherSkillForm, setShowAddOtherSkillForm] = useState(false);
    const [showAddLinkForm, setShowAddLinkForm] = useState(false);
    const [showAddSpecialSkillForm, setShowAddSpecialSkillForm] = useState(false);
    const SKILLS = CORE_SKILLS[profile?.profile_type || 'performer'];

    // Add onSubmit handler (now only accepts values)
    const onSubmit = (values: any) => {
        onSubmitForm && onSubmitForm(values);
    };

    const handleSetProfile = useCallback(() => {
        if (profile) {
            setSkills(profile?.skills ?? []);
            setOtherSkills(profile?.other_skills ?? []);
            setLanguages(profile?.language_spoken ?? []);
            setEthnicity(profile?.ethnicity || []);
            setSpecialSkills(profile?.special_skills || []);
            setLinks(profile.links || []);
            setAttributes({
                gender: profile?.gender || '',
                sex: profile?.sex || '',
                body_type: profile?.body_type || '',
                hair_color: profile?.hair_color || '',
                height: profile?.height || 0,
                weight: profile?.weight || 0,
            });
            setAbout(profile?.about || '');
            setStageName(profile?.stage_name || '');
            setLocation(profile.location);
            setAgeRange([profile?.age_from || 18, profile?.age_to || 40]);
            setTitle(profile.title || '');
        }
    }, [profile]);

    useEffect(() => {
        handleSetProfile();
    }, [handleSetProfile]);

    // Implement getSectionValues helper to return the current values for each section
    const getSectionValues = (section: string) => {
        switch (section) {
            case 'about':
                return { about };
            case 'stage_name':
                return { stage_name: stageName };
            case 'age_range':
                return { age_from: ageRange[0], age_to: ageRange[1] };
            case 'location':
                return { location };
            case 'attributes':
                return { ...attributes };
            case 'skills':
                return { skills };
            case 'other_skills':
                return { other_skills: otherSkills };
            case 'languages':
                return { language_spoken: languages };
            case 'ethnicity':
                return { ethnicity };
            case 'special_skills':
                return { special_skills: specialSkills };
            case 'links':
                return { links };
            case 'title':
                return { title };
            default:
                return {};
        }
    };

    // Helper for rendering skill chips/fields
    const renderSkillChip = (
        skill: TSkill,
        idx: number,
        type: 'skills' | 'other_skills' = 'skills',
        editing = false,
    ) => (
        <Box
            key={idx}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                bgcolor: theme[themeStyle].gray_text,
                borderRadius: 1,
                px: 1,
                py: 0.5,
                mr: 1,
                mb: 1,
            }}>
            <Typography
                sx={{ fontSize: 12, color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                {skill.skill}
            </Typography>
            <Typography sx={{ fontSize: 11, color: theme[themeStyle].light_gray_text, ml: 0.5 }}>
                ({skill.expertise}, {skill.years_of_experience} yrs)
            </Typography>
            {!previewMode && (
                <IconButton
                    size="small"
                    sx={{ ml: 0.5, p: 0.2 }}
                    onClick={() => {
                        setEditingSkillIndex(idx);
                        setNewSkill(skill);
                    }}>
                    <EditRounded fontSize="inherit" sx={{ color: theme[themeStyle].tag_white }} />
                </IconButton>
            )}
            {!previewMode && editing && (
                <IconButton
                    size="small"
                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                    onClick={() => {
                        if (type === 'skills') {
                            const newSkills = skills.filter((_, i) => i !== idx);
                            setSkills(newSkills);
                        } else {
                            const newOtherSkills = otherSkills.filter((_, i) => i !== idx);
                            setOtherSkills(newOtherSkills);
                        }
                    }}>
                    <Cancel fontSize="inherit" />
                </IconButton>
            )}
        </Box>
    );

    // Section header (move this here, above return)
    const SectionHeader = ({ title, section }: { title: string; section: string }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1rem',
                    color: theme[themeStyle].primary_white,
                }}>
                {title}
            </Typography>
            {!previewMode &&
                (editingSections[section] ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={() => {
                                // Revert back to profile values when canceling
                                switch (section) {
                                    case 'about':
                                        setAbout(profile?.about || '');
                                        break;
                                    case 'stage_name':
                                        setStageName(profile?.stage_name || '');
                                        break;
                                    case 'age_range':
                                        setAgeRange([profile?.age_from || 18, profile?.age_to || 40]);
                                        break;
                                    case 'location':
                                        setLocation(
                                            profile?.location || {
                                                address: '',
                                                description: '',
                                                coordinates: [],
                                                type: '',
                                                short_name: '',
                                            },
                                        );
                                        break;
                                    case 'attributes':
                                        setAttributes({
                                            gender: profile?.gender || '',
                                            sex: profile?.sex || '',
                                            body_type: profile?.body_type || '',
                                            hair_color: profile?.hair_color || '',
                                            height: profile?.height || 0,
                                            weight: profile?.weight || 0,
                                        });
                                        break;
                                    case 'skills':
                                        setSkills(profile?.skills || []);
                                        break;
                                    case 'other_skills':
                                        setOtherSkills(profile?.other_skills || []);
                                        break;
                                    case 'languages':
                                        setLanguages(profile?.language_spoken || []);
                                        break;
                                    case 'ethnicity':
                                        setEthnicity(profile?.ethnicity || []);
                                        break;
                                    case 'special_skills':
                                        setSpecialSkills(profile?.special_skills || []);
                                        break;
                                    case 'links':
                                        setLinks(profile?.links || []);
                                        break;
                                    case 'title':
                                        setTitle(profile?.title || '');
                                        break;
                                }
                                setEditingSections({ ...editingSections, [section]: false });
                            }}
                            sx={{
                                borderColor: theme[themeStyle].placeholderGray,
                                color: theme[themeStyle].primary_white,
                                fontSize: '0.8rem',
                                minWidth: 0,
                                px: 1,
                                '&:hover': {
                                    borderColor: theme[themeStyle].placeholderGray,
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                },
                            }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<CheckCircleOutline sx={{ color: theme[themeStyle].black }} />}
                            onClick={() => {
                                onSubmit(getSectionValues(section));
                                setEditingSections({ ...editingSections, [section]: false });
                            }}
                            sx={{
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                fontSize: '0.8rem',
                                minWidth: 0,
                                px: 1,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                            }}>
                            Save
                        </Button>
                    </Box>
                ) : (
                    <IconButton
                        onClick={() => setEditingSections({ ...editingSections, [section]: true })}
                        sx={{ color: theme[themeStyle].primary_orange, p: 0.5 }}>
                        <EditRounded fontSize="small" />
                    </IconButton>
                ))}
        </Box>
    );

    const renderProfileTitle = useMemo(() => {
        let titleName = '';
        switch (profile?.profile_type) {
            case 'artist':
                titleName = 'E.g Musician, Music Producer';
                break;
            case 'content_creator':
                titleName = 'E.g Infleuncer, Content Creator.';
                break;
            case 'performer':
                titleName = 'E.g Actor, Stage Performer.';
                break;
            case 'technical':
                titleName = 'E.g Editor, Director';
                break;
            case 'voiceover':
                titleName = 'E.g Voice Actor';
                break;
        }
        return titleName;
    }, [profile]);

    function shouldShowSection(
        profile: IWorkProfile | undefined,
        fieldKey: SectionKey,
        sectionName: string,
        isEditing: boolean,
        hasValue: boolean,
    ): boolean {
        if (!profile) return false;

        const hiddenFields = hiddenFieldsByProfileType[profile.profile_type] || [];
        const hiddenSections = hiddenSectionsByProfileType[profile.profile_type] || [];

        if (fieldKey === 'age_range' || fieldKey === 'attributes') {
            return !hiddenSections.includes(sectionName) && (isEditing || hasValue);
        }

        return !hiddenFields.includes(fieldKey) && !hiddenSections.includes(sectionName) && (isEditing || hasValue);
    }

    return (
        <Box sx={{ py: 2, px: 2, bgcolor: theme[themeStyle].pure_black, borderRadius: 2, width: '100%' }}>
            {/** Title */}
            {shouldShowSection(profile, 'title', 'title', editingSections.title, !!profile?.title) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Title" section="title" />
                        {!editingSections.title || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                    whiteSpace: 'pre-wrap',
                                }}>
                                {profile?.title || 'No title provided'}
                            </Typography>
                        ) : (
                            <TextField
                                fullWidth
                                placeholder={renderProfileTitle}
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                inputProps={{
                                    maxLength: MAX_LENGTH,
                                }}
                                helperText={`${MAX_LENGTH - title.length} characters left`}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        fontSize: 13,
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 11,
                                        textAlign: 'right',
                                    },
                                }}
                            />
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* About Section - Only show if value or in edit mode */}
            {shouldShowSection(profile, 'about', 'about', editingSections.about, !!profile?.about) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="About Me" section="about" />
                        {!editingSections.about || previewMode ? (
                            <RichTextViewer
                                content={profile?.about || 'No about info provided'}
                                textStyle={{
                                    padding: 0,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                    whiteSpace: 'pre-wrap',
                                }}
                            />
                        ) : (
                            <RichTextEditor value={about} onChange={e => setAbout(e)} />
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Social Media Section - Only for Content Creators, only show if values or in edit mode */}
            {shouldShowSection(
                profile,
                'socials',
                'socials',
                editingSections.socials,
                !!profile?.socials?.some(s => s.bio && s.followers > 0),
            ) && (
                <>
                    <SocialMediaSection
                        workProfile={profile}
                        themeStyle={themeStyle}
                        isEditMode={!previewMode}
                        previewMode={previewMode}
                        onSubmitForm={onSubmitForm}
                    />
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Stage Name Section - Hide for Content Creators */}
            {shouldShowSection(
                profile,
                'stage_name',
                'stage_name',
                editingSections.stage_name,
                !!profile?.stage_name,
            ) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Stage Name" section="stage_name" />
                        {!editingSections.stage_name || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                }}>
                                {profile?.stage_name || 'No stage name provided'}
                            </Typography>
                        ) : (
                            <TextField
                                fullWidth
                                placeholder="Stage Name"
                                value={stageName}
                                helperText={`${MAX_LENGTH - stageName.length} characters left`}
                                inputProps={{
                                    maxLength: MAX_LENGTH,
                                }}
                                onChange={e => setStageName(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        fontSize: 13,
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 11,
                                        textAlign: 'right',
                                    },
                                }}
                            />
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Age Range Section - Hide for Content Creators */}
            {shouldShowSection(
                profile,
                'age_range',
                'age_range',
                editingSections.age_range,
                profile?.age_from !== undefined || profile?.age_to !== undefined,
            ) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Age Range" section="age_range" />
                        {!editingSections.age_range || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                }}>
                                {profile?.age_from} - {profile?.age_to}
                            </Typography>
                        ) : (
                            <Box sx={{ px: 1, py: 0.5 }}>
                                <Slider
                                    value={ageRange}
                                    onChange={(_, val) => setAgeRange(val as [number, number])}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={100}
                                    sx={{
                                        color: theme[themeStyle].primary_orange,
                                        height: 2,
                                        '& .MuiSlider-thumb': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            width: 16,
                                            height: 16,
                                        },
                                    }}
                                />
                                <Typography sx={{ color: theme[themeStyle].primary_white, fontSize: 13 }}>
                                    {ageRange[0]} - {ageRange[1]}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Location Section */}
            {shouldShowSection(profile, 'location', 'location', editingSections.location, !!profile?.location) && (
                <>
                    {' '}
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Location" section="location" />
                        {!editingSections.location || previewMode ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                    fontSize: 13,
                                }}>
                                {profile?.location?.address || 'No location provided'}
                            </Typography>
                        ) : (
                            <Box sx={{ mt: 0.5 }}>
                                <LocationAutocomplete
                                    value={location}
                                    onSelect={(address, coordinates) =>
                                        setLocation({ ...location, address, coordinates: coordinates || [] })
                                    }
                                    withCordinates
                                    onSelectLocationOption={() => {}}
                                    searchOptions={{ types: ['(regions)'] }}
                                    placeholder="Search region or country..."
                                    fieldStyle={{
                                        bgcolor: theme[themeStyle].gray_text,
                                        borderRadius: '8px',
                                        input: { color: theme[themeStyle].tag_white },
                                        marginTop: '0px',
                                        label: { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Attributes Section - Hide for Content Creators */}
            {shouldShowSection(
                profile,
                'attributes',
                'attributes',
                editingSections.attributes,
                !!(
                    profile?.gender ||
                    profile?.sex ||
                    profile?.body_type ||
                    profile?.hair_color ||
                    profile?.height ||
                    profile?.weight
                ),
            ) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Attributes" section="attributes" />
                        {/* ...attributes section content... */}
                        {!editingSections.attributes || previewMode ? (
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Gender
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.gender || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Sex
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.sex || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Body Type
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.body_type || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Hair Color
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.hair_color || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Height (cm)
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.height || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 12,
                                        }}>
                                        Weight (lbs)
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                            fontSize: 13,
                                        }}>
                                        {profile?.weight || '-'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Gender"
                                        value={attributes.gender}
                                        onChange={e => setAttributes(a => ({ ...a, gender: e.target.value }))}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].gray_text,
                                                fontSize: 13,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Sex"
                                        value={attributes.sex}
                                        onChange={e => setAttributes(a => ({ ...a, sex: e.target.value }))}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].gray_text,
                                                fontSize: 13,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Body Type"
                                        value={attributes.body_type}
                                        onChange={e => setAttributes(a => ({ ...a, body_type: e.target.value }))}
                                        fullWidth
                                        select
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].gray_text,
                                                fontSize: 13,
                                            },
                                        }}>
                                        {ATTR_BODY_TYPES.map(type => (
                                            <MenuItem key={type} value={type}>
                                                {type.charAt(0).toUpperCase() + type.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Hair Color"
                                        value={attributes.hair_color}
                                        onChange={e => setAttributes(a => ({ ...a, hair_color: e.target.value }))}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].gray_text,
                                                fontSize: 13,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Height (cm)"
                                        type="number"
                                        value={attributes.height}
                                        onChange={e => setAttributes(a => ({ ...a, height: Number(e.target.value) }))}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].gray_text,
                                                fontSize: 13,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Weight (lbs)"
                                        type="number"
                                        value={attributes.weight}
                                        onChange={e => setAttributes(a => ({ ...a, weight: Number(e.target.value) }))}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].gray_text,
                                                fontSize: 13,
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Skills Section */}
            {shouldShowSection(profile, 'skills', 'skills', editingSections.skills, !!profile?.skills) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Skills" section="skills" />
                        {!editingSections.skills || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {skills.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No skills
                                    </Typography>
                                ) : (
                                    skills.map((skill, idx) =>
                                        renderSkillChip(skill, idx, 'skills', editingSections.skills),
                                    )
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {skills.map((skill, idx) =>
                                        editingSkillIndex === idx ? (
                                            <Box
                                                key={idx}
                                                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                                <Autocomplete
                                                    value={newSkill.skill}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            setNewSkill(s => ({ ...s, skill: newValue }));
                                                        }
                                                    }}
                                                    inputValue={newSkill.skill}
                                                    onInputChange={(_, newInputValue) =>
                                                        setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                    }
                                                    popupIcon={
                                                        <ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />
                                                    }
                                                    componentsProps={{
                                                        paper: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                                color: theme[themeStyle].primary_white,
                                                                '& .MuiAutocomplete-option': {
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].background_gradient.end,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    options={SKILLS.filter(
                                                        s => !skills.some(existing => existing.skill === s),
                                                    )}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="Skill"
                                                            sx={{
                                                                width: 200,
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme[themeStyle].gray_text,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    color: theme[themeStyle].tag_white,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <TextField
                                                    size="small"
                                                    select
                                                    label="Expertise"
                                                    value={newSkill.expertise}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, expertise: e.target.value as any }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}>
                                                    <MenuItem value="beginner">Beginner</MenuItem>
                                                    <MenuItem value="mid-level">Mid-level</MenuItem>
                                                    <MenuItem value="expert">Expert</MenuItem>
                                                </TextField>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    label="Years of Experience"
                                                    value={newSkill.years_of_experience}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e =>
                                                        setNewSkill(s => ({
                                                            ...s,
                                                            years_of_experience: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="Description"
                                                    value={newSkill.description}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, description: e.target.value }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => {
                                                        setSkills(skills.map((s, i) => (i === idx ? newSkill : s)));
                                                        setEditingSkillIndex(null);
                                                    }}>
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setEditingSkillIndex(null)}>
                                                    Cancel
                                                </Button>
                                            </Box>
                                        ) : (
                                            renderSkillChip(skill, idx, 'skills', editingSections.skills)
                                        ),
                                    )}
                                </Box>
                                {!showAddSkillForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddSkillForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Skill
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Skill
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <Autocomplete
                                                    value={newSkill.skill}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            setNewSkill(s => ({ ...s, skill: newValue }));
                                                        }
                                                    }}
                                                    inputValue={newSkill.skill}
                                                    onInputChange={(_, newInputValue) =>
                                                        setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                    }
                                                    options={SKILLS.filter(
                                                        s => !skills.some(existing => existing.skill === s),
                                                    )}
                                                    popupIcon={
                                                        <ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />
                                                    }
                                                    componentsProps={{
                                                        paper: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                                color: theme[themeStyle].primary_white,
                                                                '& .MuiAutocomplete-option': {
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].background_gradient.end,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="Skill"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme[themeStyle].gray_text,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    color: theme[themeStyle].tag_white,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    select
                                                    label="Expertise"
                                                    value={newSkill.expertise}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, expertise: e.target.value as any }))
                                                    }
                                                    fullWidth
                                                    SelectProps={{
                                                        MenuProps: {
                                                            PaperProps: {
                                                                sx: {
                                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '& .MuiMenuItem-root': {
                                                                        color: theme[themeStyle].primary_white,
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].highlight,
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].primary_orange,
                                                                            color: theme[themeStyle].black,
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                    theme[themeStyle].primary_orange,
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}>
                                                    <MenuItem value="beginner">Beginner</MenuItem>
                                                    <MenuItem value="mid-level">Mid-level</MenuItem>
                                                    <MenuItem value="expert">Expert</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    label="Years of Experience"
                                                    value={newSkill.years_of_experience}
                                                    onChange={e =>
                                                        setNewSkill(s => ({
                                                            ...s,
                                                            years_of_experience: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    label="Description"
                                                    value={newSkill.description}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, description: e.target.value }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddSkillForm(false);
                                                    setNewSkill({
                                                        skill: '',
                                                        expertise: 'beginner',
                                                        years_of_experience: 1,
                                                        description: '',
                                                    });
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newSkill.skill) {
                                                        setSkills([...skills, newSkill]);
                                                        setNewSkill({
                                                            skill: '',
                                                            expertise: 'beginner',
                                                            years_of_experience: 1,
                                                            description: '',
                                                        });
                                                        setShowAddSkillForm(false);
                                                    }
                                                }}>
                                                Add Skill
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Other Skills Section */}
            {shouldShowSection(
                profile,
                'other_skills',
                'other_skills',
                editingSections.other_skills,
                !!profile?.other_skills,
            ) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Other Skills" section="other_skills" />
                        {!editingSections.other_skills || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {otherSkills.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No other skills
                                    </Typography>
                                ) : (
                                    otherSkills.map((skill, idx) =>
                                        renderSkillChip(skill, idx, 'other_skills', editingSections.other_skills),
                                    )
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {otherSkills.map((skill, idx) => (
                                        <Box key={idx}>
                                            {editingSkillIndex === idx ? (
                                                <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                                    <Autocomplete
                                                        value={newSkill.skill}
                                                        onChange={(_, newValue) => {
                                                            if (newValue) {
                                                                setNewSkill(s => ({ ...s, skill: newValue }));
                                                            }
                                                        }}
                                                        inputValue={newSkill.skill}
                                                        onInputChange={(_, newInputValue) =>
                                                            setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                        }
                                                        options={SKILLS.filter(
                                                            s => !otherSkills.some(existing => existing.skill === s),
                                                        )}
                                                        popupIcon={
                                                            <ArrowDropDown
                                                                sx={{ color: theme[themeStyle].tag_white }}
                                                            />
                                                        }
                                                        componentsProps={{
                                                            paper: {
                                                                sx: {
                                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '& .MuiAutocomplete-option': {
                                                                        color: theme[themeStyle].primary_white,
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].highlight,
                                                                        },
                                                                        '&.Mui-focused': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].background_gradient
                                                                                    .end,
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].primary_orange,
                                                                            color: theme[themeStyle].black,
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                label="Skill"
                                                                fullWidth
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        backgroundColor: theme[themeStyle].gray_text,
                                                                        color: theme[themeStyle].tag_white,
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                        '& fieldset': {
                                                                            borderColor:
                                                                                theme[themeStyle].placeholderGray50,
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor:
                                                                                theme[themeStyle].placeholderGray50,
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: theme[themeStyle].highlight,
                                                                        },
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        color: theme[themeStyle].tag_white,
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    },
                                                                    '& .MuiInputLabel-root': {
                                                                        color: theme[themeStyle].tag_white,
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        select
                                                        label="Expertise"
                                                        value={newSkill.expertise}
                                                        onChange={e =>
                                                            setNewSkill(s => ({
                                                                ...s,
                                                                expertise: e.target.value as any,
                                                            }))
                                                        }
                                                        fullWidth
                                                        SelectProps={{
                                                            MenuProps: {
                                                                PaperProps: {
                                                                    sx: {
                                                                        backgroundColor: theme[themeStyle].secondaryBg,
                                                                        color: theme[themeStyle].primary_white,
                                                                        '& .MuiMenuItem-root': {
                                                                            color: theme[themeStyle].primary_white,
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                    theme[themeStyle].highlight,
                                                                            },
                                                                            '&.Mui-selected': {
                                                                                backgroundColor:
                                                                                    theme[themeStyle].primary_orange,
                                                                                color: theme[themeStyle].black,
                                                                                '&:hover': {
                                                                                    backgroundColor:
                                                                                        theme[themeStyle]
                                                                                            .primary_orange,
                                                                                },
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '& fieldset': {
                                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: theme[themeStyle].tag_white,
                                                            },
                                                        }}>
                                                        <MenuItem value="beginner">Beginner</MenuItem>
                                                        <MenuItem value="mid-level">Mid-level</MenuItem>
                                                        <MenuItem value="expert">Expert</MenuItem>
                                                    </TextField>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        label="Years of Experience"
                                                        value={newSkill.years_of_experience}
                                                        onChange={e =>
                                                            setNewSkill(s => ({
                                                                ...s,
                                                                years_of_experience: Number(e.target.value),
                                                            }))
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '& fieldset': {
                                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: theme[themeStyle].tag_white,
                                                            },
                                                        }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        label="Description"
                                                        value={newSkill.description}
                                                        onChange={e =>
                                                            setNewSkill(s => ({ ...s, description: e.target.value }))
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                '& fieldset': {
                                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                color: theme[themeStyle].tag_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: theme[themeStyle].tag_white,
                                                            },
                                                        }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => {
                                                            setOtherSkills(
                                                                otherSkills.map((s, i) => (i === idx ? newSkill : s)),
                                                            );
                                                            onSubmit({
                                                                other_skills: otherSkills.map((s, i) =>
                                                                    i === idx ? newSkill : s,
                                                                ),
                                                            });
                                                            setEditingSkillIndex(null);
                                                        }}>
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setEditingSkillIndex(null);
                                                            setNewSkill({
                                                                skill: '',
                                                                expertise: 'beginner',
                                                                years_of_experience: 1,
                                                                description: '',
                                                            });
                                                        }}>
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            ) : (
                                                renderSkillChip(
                                                    skill,
                                                    idx,
                                                    'other_skills',
                                                    editingSections.other_skills,
                                                )
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                {!showAddOtherSkillForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddOtherSkillForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Other Skill
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Other Skill
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <Autocomplete
                                                    value={newSkill.skill}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            setNewSkill(s => ({ ...s, skill: newValue }));
                                                        }
                                                    }}
                                                    inputValue={newSkill.skill}
                                                    onInputChange={(_, newInputValue) =>
                                                        setNewSkill(s => ({ ...s, skill: newInputValue }))
                                                    }
                                                    options={SKILLS.filter(
                                                        s => !otherSkills.some(existing => existing.skill === s),
                                                    )}
                                                    popupIcon={
                                                        <ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />
                                                    }
                                                    componentsProps={{
                                                        paper: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                                color: theme[themeStyle].primary_white,
                                                                '& .MuiAutocomplete-option': {
                                                                    color: theme[themeStyle].primary_white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].highlight,
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].background_gradient.end,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor:
                                                                            theme[themeStyle].primary_orange,
                                                                        color: theme[themeStyle].black,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="Skill"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme[themeStyle].gray_text,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    '& fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor:
                                                                            theme[themeStyle].placeholderGray50,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    color: theme[themeStyle].tag_white,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    select
                                                    label="Expertise"
                                                    value={newSkill.expertise}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, expertise: e.target.value as any }))
                                                    }
                                                    fullWidth
                                                    SelectProps={{
                                                        MenuProps: {
                                                            PaperProps: {
                                                                sx: {
                                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                                    color: theme[themeStyle].primary_white,
                                                                    '& .MuiMenuItem-root': {
                                                                        color: theme[themeStyle].primary_white,
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].highlight,
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].primary_orange,
                                                                            color: theme[themeStyle].black,
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                    theme[themeStyle].primary_orange,
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}>
                                                    <MenuItem value="beginner">Beginner</MenuItem>
                                                    <MenuItem value="mid-level">Mid-level</MenuItem>
                                                    <MenuItem value="expert">Expert</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    label="Years of Experience"
                                                    value={newSkill.years_of_experience}
                                                    onChange={e =>
                                                        setNewSkill(s => ({
                                                            ...s,
                                                            years_of_experience: Number(e.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    size="small"
                                                    label="Description"
                                                    value={newSkill.description}
                                                    onChange={e =>
                                                        setNewSkill(s => ({ ...s, description: e.target.value }))
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddOtherSkillForm(false);
                                                    setNewSkill({
                                                        skill: '',
                                                        expertise: 'beginner',
                                                        years_of_experience: 1,
                                                        description: '',
                                                    });
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newSkill.skill) {
                                                        setOtherSkills([...otherSkills, newSkill]);
                                                        setNewSkill({
                                                            skill: '',
                                                            expertise: 'beginner',
                                                            years_of_experience: 1,
                                                            description: '',
                                                        });
                                                        setShowAddOtherSkillForm(false);
                                                    }
                                                }}>
                                                Add Skill
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Languages Section */}
            {shouldShowSection(
                profile,
                'language_spoken',
                'language_spoken',
                editingSections.language_spoken,
                !!profile?.language_spoken,
            ) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Languages" section="languages" />
                        {!editingSections.languages || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {languages.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No languages
                                    </Typography>
                                ) : (
                                    languages.map((lang, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme[themeStyle].primary_white,
                                                    textTransform: 'capitalize',
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                {lang}
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {languages.map((lang, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme[themeStyle].primary_white,
                                                    textTransform: 'capitalize',
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                {lang}
                                            </Typography>
                                            {editingSections.languages && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() => {
                                                        const newLanguages = languages.filter((_, i) => i !== idx);
                                                        setLanguages(newLanguages);
                                                        onSubmit({ language_spoken: newLanguages });
                                                    }}>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                    <Autocomplete
                                        value={newLanguage}
                                        onChange={(_, newValue) => {
                                            if (newValue && !languages.includes(newValue)) {
                                                setLanguages([...languages, newValue]);
                                            }
                                        }}
                                        inputValue={newLanguage}
                                        onInputChange={(_, newInputValue) => setNewLanguage(newInputValue)}
                                        options={LANGUAGES.filter(lang => !languages.includes(lang))}
                                        popupIcon={<ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />}
                                        componentsProps={{
                                            paper: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiAutocomplete-option': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Add Language"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Ethnicity Section */}
            {shouldShowSection(profile, 'ethnicity', 'ethnicity', editingSections.ethnicity, !!profile?.ethnicity) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Ethnicity" section="ethnicity" />
                        {!editingSections.ethnicity || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {ethnicity.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No ethnicity
                                    </Typography>
                                ) : (
                                    ethnicity.map((eth, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                            }}>
                                            <Typography sx={{ fontSize: 12, color: theme[themeStyle].primary_white }}>
                                                {eth}
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {ethnicity.map((eth, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                            <Typography sx={{ fontSize: 12, color: theme[themeStyle].primary_white }}>
                                                {eth}
                                            </Typography>
                                            {editingSections.ethnicity && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() => {
                                                        const newEthnicity = ethnicity.filter((_, i) => i !== idx);
                                                        setEthnicity(newEthnicity);
                                                        onSubmit({ ethnicity: newEthnicity });
                                                    }}>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                    <Autocomplete
                                        value={newEthnicity}
                                        onChange={(_, newValue) => {
                                            if (newValue && !ethnicity.includes(newValue)) {
                                                setEthnicity([...ethnicity, newValue]);
                                            }
                                        }}
                                        inputValue={newEthnicity}
                                        onInputChange={(_, newInputValue) => setNewEthnicity(newInputValue)}
                                        options={ETHNICITIES.filter(eth => !ethnicity.includes(eth))}
                                        popupIcon={<ArrowDropDown sx={{ color: theme[themeStyle].tag_white }} />}
                                        componentsProps={{
                                            paper: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].secondaryBg,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiAutocomplete-option': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Add Ethnicity"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Special Skills Section */}
            {shouldShowSection(
                profile,
                'special_skills',
                'special_skills',
                editingSections.special_skills,
                !!profile?.special_skills,
            ) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Special Skills" section="special_skills" />
                        {!editingSections.special_skills || previewMode ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {specialSkills.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No special skills
                                    </Typography>
                                ) : (
                                    specialSkills.map((skill, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme[themeStyle].primary_white,
                                                    textTransform: 'capitalize',
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                {skill}
                                            </Typography>
                                            {editingSections.special_skills && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() =>
                                                        setSpecialSkills(specialSkills.filter((_, i) => i !== idx))
                                                    }>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {specialSkills.map((skill, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                bgcolor: theme[themeStyle].gray_text,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 0.5,
                                                mr: 1,
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: theme[themeStyle].primary_white,
                                                    textTransform: 'capitalize',
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                {skill}
                                            </Typography>
                                            {editingSections.special_skills && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() =>
                                                        setSpecialSkills(specialSkills.filter((_, i) => i !== idx))
                                                    }>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                {!showAddSpecialSkillForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddSpecialSkillForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Special Skill
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Special Skill
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    size="small"
                                                    label="Special Skill"
                                                    value={newSpecialSkill}
                                                    InputLabelProps={{
                                                        sx: {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                    onChange={e => setNewSpecialSkill(e.target.value)}
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddSpecialSkillForm(false);
                                                    setNewSpecialSkill('');
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newSpecialSkill) {
                                                        setSpecialSkills([...specialSkills, newSpecialSkill]);
                                                        setNewSpecialSkill('');
                                                        setShowAddSpecialSkillForm(false);
                                                    }
                                                }}>
                                                Add Skill
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}

            {/* Links Section */}
            {shouldShowSection(profile, 'links', 'links', editingSections.links, !!profile?.links) && (
                <>
                    <Box sx={{ mb: 2 }}>
                        <SectionHeader title="Links" section="links" />
                        {!editingSections.links || previewMode ? (
                            <Box>
                                {links.length === 0 ? (
                                    <Typography sx={{ fontSize: 13, color: theme[themeStyle].light_gray_text }}>
                                        No links
                                    </Typography>
                                ) : (
                                    links.map((l, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography
                                                sx={{ fontSize: 12, color: theme[themeStyle].primary_white, mr: 1 }}>
                                                <a
                                                    href={l.description}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: theme[themeStyle].highlight,
                                                        textDecoration: 'underline',
                                                    }}>
                                                    {l.link}
                                                </a>
                                                {l.description && (
                                                    <span
                                                        style={{
                                                            color: theme[themeStyle].light_gray_text,
                                                            marginLeft: 8,
                                                        }}>
                                                        ({l.description})
                                                    </span>
                                                )}
                                            </Typography>
                                            {editingSections.links && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() => setLinks(links.filter((_, i) => i !== idx))}>
                                                    <Cancel fontSize="inherit" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                    {links.map((l, idx) =>
                                        editingLinkIndex === idx ? (
                                            <Box
                                                key={idx}
                                                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                                                <TextField
                                                    size="small"
                                                    label="Link"
                                                    value={newLink.link}
                                                    onChange={e => setNewLink(s => ({ ...s, link: e.target.value }))}
                                                    sx={{
                                                        flex: 1,
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="Description"
                                                    value={newLink.description}
                                                    onChange={e =>
                                                        setNewLink(s => ({ ...s, description: e.target.value }))
                                                    }
                                                    sx={{
                                                        flex: 1,
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme[themeStyle].tag_white,
                                                        },
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => {
                                                        if (newLink.link) {
                                                            setLinks([...links, newLink]);
                                                            setNewLink({ link: '', description: '' });
                                                            setShowAddLinkForm(false);
                                                        }
                                                    }}>
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setEditingLinkIndex(null);
                                                        setNewLink({ link: '', description: '' });
                                                    }}>
                                                    Cancel
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: theme[themeStyle].primary_white,
                                                        mr: 1,
                                                    }}>
                                                    <a
                                                        href={l.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: theme[themeStyle].highlight,
                                                            textDecoration: 'underline',
                                                        }}>
                                                        {l.link}
                                                    </a>
                                                    {l.description && (
                                                        <span
                                                            style={{
                                                                color: theme[themeStyle].light_gray_text,
                                                                marginLeft: 8,
                                                            }}>
                                                            ({l.description})
                                                        </span>
                                                    )}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                    onClick={() => {
                                                        setEditingLinkIndex(idx);
                                                        setNewLink(l);
                                                    }}>
                                                    <EditRounded fontSize="inherit" />
                                                </IconButton>
                                                {editingSections.links && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ ml: 0.2, p: 0.2, color: theme[themeStyle].highlight }}
                                                        onClick={() => setLinks(links.filter((_, i) => i !== idx))}>
                                                        <Cancel fontSize="inherit" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ),
                                    )}
                                </Box>
                                {!showAddLinkForm ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddCircleOutline />}
                                        onClick={() => setShowAddLinkForm(true)}
                                        sx={{
                                            borderColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].primary_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                            },
                                        }}>
                                        Add New Link
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            mt: 1,
                                            p: 2,
                                            bgcolor: theme[themeStyle].gray_text,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 14, color: theme[themeStyle].primary_white, mb: 1 }}>
                                            Add New Link
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <TextField
                                                size="small"
                                                label="Link"
                                                value={newLink.link}
                                                onChange={e => setNewLink(s => ({ ...s, link: e.target.value }))}
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].grey,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].tag_white,
                                                        },
                                                    },
                                                }}
                                                InputProps={{
                                                    style: {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    style: {
                                                        color: theme[themeStyle].grey,
                                                    },
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                label="Description"
                                                value={newLink.description}
                                                onChange={e => setNewLink(s => ({ ...s, description: e.target.value }))}
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].grey,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].tag_white,
                                                        },
                                                    },
                                                }}
                                                InputProps={{
                                                    style: {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    style: {
                                                        color: theme[themeStyle].grey,
                                                    },
                                                }}
                                            />
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    if (newLink.link) {
                                                        setLinks([...links, newLink]);
                                                        setNewLink({ link: '', description: '' });
                                                        setShowAddLinkForm(false);
                                                    }
                                                }}>
                                                Add
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowAddLinkForm(false);
                                                    setNewLink({ link: '', description: '' });
                                                }}>
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Divider sx={{ my: 2, borderColor: theme[themeStyle].grey }} />
                </>
            )}
        </Box>
    );
};

export default CastingDetails;
